import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import { ClassOutlined } from "@mui/icons-material";
import CustomDatePicker from "../../common/CustomDatePicker";
import { RecordPastOrder } from "../../../redux/slice/product/createProductSlice";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { ORDERTYPE } from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { useModalStyles } from "../../common/useModalStyles";
import { History } from "lucide-react";

interface RecordPastOrderPopupProps {
  open: boolean;
  patientId?: number | null;
  practiceId?: number | null;
  practiceName?: number | null;
  patientData?: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    dob?: string | null;
    gender?: string | null;
  };
  onClose: () => void;
}

const RecordPastOrderPopup: React.FC<RecordPastOrderPopupProps> = ({
  open,
  onClose,
  patientId,
  practiceId,
  practiceName,
  patientData,
}) => {
  const { navigate } = useNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { control, handleSubmit, reset, setValue } = useForm<RecordPastOrder>();
  const [isDisableCreateButton, setIsDisableCreateButton] = useState(true);
  const [orderDate, setOrderDate] = useState("");

  const handleClose = () => {
    setOrderDate("");
    reset();
    setIsDisableCreateButton(true);
    onClose();
  };

  const handleDateChangeOrder = (date: Date | null) => {
    if (date) {
      const formattedDate = dayjs(date).format("MM/DD/YYYY");
      setOrderDate(formattedDate);
    } else {
      setOrderDate("");
    }
  };

  useEffect(() => {
    if (orderDate !== "") {
      setIsDisableCreateButton(false);
    } else {
      setIsDisableCreateButton(true);
    }
  }, [orderDate]);

  const handleSave = async () => {
    navigate("/order", {
      state: {
        practiceId,
        practiceName,
        patientId,
        patientData,
        orderType: ORDERTYPE.PAST_ORDER,
        orderDate,
      },
    });
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      onClick={(event) => {
        event.stopPropagation();
      }}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <History size={22} color={modalTokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: modalTokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Record Past Order
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
            Choose the date the order was placed
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={{ ...bodySx, minHeight: 0 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: modalTokens.textPrimary, mb: 1 }}>
          When was the order placed?
        </Typography>
        <Box sx={datepickerWrapSx(false)}>
          <CustomDatePicker
            isDateCalendar={false}
            control={control}
            controllerName="OrderPlaceDate"
            label="Order place date"
            onChange={handleDateChangeOrder}
          />
        </Box>
      </Box>

      <Box sx={footerSx}>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            disabled={isDisableCreateButton}
            onClick={handleSave}
            startIcon={<ClassOutlined sx={{ fontSize: "1rem !important" }} />}
            sx={{
              ...primaryBtnSx,
              backgroundColor: !isDisableCreateButton ? modalTokens.accent : undefined,
              boxShadow: !isDisableCreateButton ? "0 4px 18px rgba(20,184,166,0.35)" : "none",
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                color: theme.palette.mode === "dark" ? "#3d4a5c" : "#b0bec5",
              },
            }}
          >
            Record
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RecordPastOrderPopup;
